<script setup>
import { inject, computed } from 'vue'
import { usePage } from '@inertiajs/vue3'

const __t = inject('__t')
const page = usePage()
const isRtl = computed(() => page.props.locale === 'ar')

// Title & subtitle use existing translation keys
const aboutTitle = __t('home_about_title')
const aboutSubtitle = __t('home_about_subtitle')

// Section paragraphs — content from the reference design
const paragraphs = computed(() => [
    __t('home_about_desc_1'),
    __t('home_about_desc_2'),
    __t('home_about_desc_3'),
])
</script>

<template>
    <section class="dark:bg-gray-950">

        <!-- Top: Split Layout Header (Text half / Image half) -->
        <div class="w-full bg-primary dark:bg-gray-900 overflow-hidden relative">
            <div class="container mx-auto px-6 lg:px-12 py-16 md:py-24">
                <div class="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

                    <!-- Content Side: Takes 50% on MD+ screens -->
                    <div :class="[
                        'w-full lg:w-1/2 flex items-center',
                        isRtl ? 'lg:order-2' : 'lg:order-1'
                    ]">
                        <div class="max-w-lg w-full" :data-aos="isRtl ? 'fade-left' : 'fade-right'">
                            <!-- Decoration line -->
                            <div :class="['w-20 h-1.5 bg-secondary mb-8 rounded-full', isRtl ? 'ml-auto' : 'mr-auto']"></div>

                            <h2 class="text-white font-black leading-tight" :class="isRtl ? 'text-right' : 'text-left'"
                                style="font-size: clamp(2.9rem, 5vw, 4.9rem);">
                                {{ aboutTitle }}
                                <span class="block text-secondary mt-4 opacity-90"
                                    style="font-size: 0.65em; letter-spacing: 0.02em;">
                                    {{ aboutSubtitle }}
                                </span>
                            </h2>
                        </div>
                    </div>

                    <!-- Image Side: Takes 50% on MD+ screens -->
                    <div :class="[
                        'w-full lg:w-1/2 flex justify-center lg:justify-end',
                        isRtl ? 'lg:order-1' : 'lg:order-2'
                    ]">
                        <div class="relative group w-full aspect-[4/3] lg:aspect-auto min-h-[300px] lg:h-[400px] max-w-2xl">
                            <!-- Top-left rounded corner as per design -->
                            <div class="h-full w-full rounded-tl-[100px] rounded-br-[40px] overflow-hidden shadow-2xl relative">
                                <img src="/assets/images/r419112017-779x405.png"
                                    class="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                                    :alt="__t('home_about_image_alt')" />
                                <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                            </div>
                            <!-- Border decoration -->
                            <div
                                class="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 border-r-8 border-b-8 border-secondary rounded-br-[40px] pointer-events-none">
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <!-- Bottom: White text section -->
        <div class="bg-primary text-white dark:bg-gray-900 py-12 px-6 md:px-14 lg:px-24">
            <div class="max-w-5xl mx-auto" :class="isRtl ? 'text-right' : 'text-left'">

                <!-- Paragraph 1 -->
                <div class="mb-8"
                    :class="isRtl ? 'border-r-4 border-secondary pr-5' : 'border-l-4 border-secondary pl-5'"
                    data-aos="fade-up" data-aos-delay="100">
                    <p class="text-white dark:text-gray-300 leading-[2.2] text-[1.25rem] font-medium">
                        {{ paragraphs[0] }}
                    </p>
                </div>

                <!-- Paragraph 2 -->
                <div class="mb-8"
                    :class="isRtl ? 'border-r-4 border-secondary pr-5' : 'border-l-4 border-secondary pl-5'"
                    data-aos="fade-up" data-aos-delay="200">
                    <p class="text-white dark:text-gray-300 leading-[2.2] text-[1.25rem] font-medium">
                        {{ paragraphs[1] }}
                    </p>
                </div>

                <!-- Paragraph 3 -->
                <div class="mb-10"
                    :class="isRtl ? 'border-r-4 border-secondary pr-5' : 'border-l-4 border-secondary pl-5'"
                    data-aos="fade-up" data-aos-delay="300">
                    <p class="text-white dark:text-gray-300 leading-[2.2] text-[1.25rem] font-medium">
                        {{ paragraphs[2] }}
                    </p>
                </div>

                <!-- Stats Row -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pt-12 mt-12 border-t border-white/10"
                    data-aos="fade-up" data-aos-delay="400">
                    <div
                        class="bg-white/5 backdrop-blur-sm p-8 md:p-10 rounded-[2.5rem] md:rounded-[3rem] border border-white/10 hover:bg-white/10 transition-all duration-500 group shadow-xl">
                        <div class="flex items-center justify-between" :class="isRtl ? 'text-right' : 'text-left'">
                            <div class="flex flex-col">
                                <span
                                    class="font-black text-secondary block mb-2 text-5xl md:text-6xl tracking-tight group-hover:scale-110 transition-transform duration-500"
                                    :class="isRtl ? 'origin-right' : 'origin-left'">1954</span>
                                <span
                                    class="text-gray-300 dark:text-gray-400 font-bold uppercase tracking-widest text-xs md:text-sm opacity-80">
                                    {{ __t('home_foundation_year') }}
                                </span>
                            </div>
                            <img src="/assets/1+++.png" class="w-24 h-24" alt="Foundation icon" />
                        </div>
                    </div>
                    <div
                        class="bg-white/5 backdrop-blur-sm p-8 md:p-10 rounded-[2.5rem] md:rounded-[3rem] border border-white/10 hover:bg-white/10 transition-all duration-500 group shadow-xl">
                        <div class="flex items-center justify-between" :class="isRtl ? 'text-right' : 'text-left'">
                            <div class="flex flex-col">
                                <span
                                    class="font-black text-secondary block mb-2 text-5xl md:text-6xl tracking-tight group-hover:scale-110 transition-transform duration-500"
                                    :class="isRtl ? 'origin-right' : 'origin-left'">+70</span>
                                <span
                                    class="text-gray-300 dark:text-gray-400 font-bold uppercase tracking-widest text-xs md:text-sm opacity-80">
                                    {{ __t('home_years_experience') }}
                                </span>
                            </div>
                            <img src="/assets/1++.png" class="w-24 h-24" alt="Experience icon" />
                        </div>
                    </div>
                </div>

            </div>
        </div>

        <!-- New Section: Qualitative Shift -->
        <div
            class="bg-white dark:bg-gray-950 pt-20 pb-10 px-6 md:px-14 lg:px-24 border-t border-gray-100 dark:border-gray-800">
            <div class="max-w-6xl mx-auto text-center" data-aos="fade-up">
                <h3 class="text-primary dark:text-white font-black leading-tight mb-6"
                    style="font-size: clamp(2.9rem, 6vw, 4.0rem);">
                    {{ __t('home_about_shift_title') }}
                </h3>
                <div class="w-24 h-1.5 bg-secondary mx-auto rounded-full"></div>
            </div>
        </div>

        <!-- Full Width Description Block -->
        <div class="bg-primary dark:bg-gray-900 py-20 md:py-24 px-6 md:px-14 lg:px-24 shadow-inner" data-aos="f ade-up">
            <div class="max-w-6xl mx-auto">
                <p class="text-white dark:text-gray-200 text-[1.05rem] md:text-[1.4rem] text-center leading-[2.3] font-medium"
                    :class="isRtl ? 'text-right' : 'text-left'">
                    {{ __t('home_about_shift_desc') }}
                </p>
            </div>
        </div>

        <!-- Full Width Image Section -->
        <div class="w-full overflow-hidden p-9 px-12 bg-primary" data-aos="zoom-out">
            <img src="/assets/almatin/media__1775636178205.png"
                class="w-full  object-cover transition-transform duration-[2000ms] "
                :alt="__t('home_about_shift_title')" />
        </div>

    </section>
</template>
